import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCustomerEnrollment, fetchPayments, updateEnrollmentPaymentPlan } from "../customerSchemeApi";
import {
  PAYMENT_FREQUENCIES,
  formatDisplayDate,
  getEnrollmentPaymentDetails,
  normalizePositiveNumber,
} from "../paymentPlanUtils";
import "../CustomerScheme.css";

const numberValue = (value) => Number(value || 0);
const getMessage = (err, fallback) =>
  err?.response?.data?.msg || err?.response?.data?.message || fallback;

const getInstallmentNumber = (item, index) =>
  Number(item?.installmentNo || item?.installmentNumber || index + 1);

const getPaymentIdValue = (value) => String(value?.$oid || value || "");

const normalizePaymentStatus = (status) => String(status || "").toUpperCase();

const chooseStrongerStatus = (current, incoming) => {
  const priority = {
    REJECTED: 4,
    PENDING: 3,
    APPROVED: 2,
    SUCCESS: 2,
    PAID: 2,
    "": 0,
  };
  const currentValue = normalizePaymentStatus(current);
  const incomingValue = normalizePaymentStatus(incoming);
  return (priority[incomingValue] || 0) >= (priority[currentValue] || 0)
    ? incomingValue
    : currentValue;
};

const getInstallmentStatusMeta = (installment, installmentNo, paymentStatusById, paymentStatusByInstallmentNo) => {
  const rawInstallmentStatus = String(installment?.status || "PENDING").toUpperCase();
  if (rawInstallmentStatus === "PAID") {
    return {
      label: "Paid",
      className: "customer-status customer-status-paid",
      isPaid: true,
    };
  }

  const linkedPaymentId = getPaymentIdValue(installment?.paymentId);
  const linkedPaymentStatus = linkedPaymentId
    ? normalizePaymentStatus(paymentStatusById[linkedPaymentId])
    : normalizePaymentStatus(paymentStatusByInstallmentNo[installmentNo]);

  if (linkedPaymentStatus === "REJECTED") {
    return {
      label: "Rejected",
      className: "customer-status customer-status-rejected",
      isPaid: false,
    };
  }

  if (linkedPaymentStatus === "SUCCESS" || linkedPaymentStatus === "APPROVED" || linkedPaymentStatus === "PAID") {
    return {
      label: "Paid",
      className: "customer-status customer-status-paid",
      isPaid: true,
    };
  }

  if (linkedPaymentStatus === "PENDING" || linkedPaymentId) {
    return {
      label: "Waiting for Approval",
      className: "customer-status customer-status-pending",
      isPaid: false,
    };
  }

  return {
    label: "Pending",
    className: "customer-status customer-status-pending",
    isPaid: false,
  };
};

const getDisplayStatus = (status) => {
  const value = String(status || "PENDING").toUpperCase();
  if (value === "PENDING") {
    return "Waiting for Approval";
  }
  if (value === "SUCCESS") {
    return "Approved";
  }
  return status || "PENDING";
};

const getApplicationNumber = (item) =>
  item?.applicationNumber || item?.applicationNo || item?.appNo || "";

const getCycleUnitLabel = (frequency) => {
  const value = String(frequency || "").toUpperCase();
  if (value === "DAILY") return "Days";
  if (value === "WEEKLY") return "Weeks";
  return "Months";
};

const getPlanTypeLabel = (value) => {
  const normalized = String(value || "").toUpperCase();
  if (normalized === "OPEN_VALUE") return "SAVINGS_SCHEME";
  return normalized || "-";
};

const normalizeEnrollments = (raw) => {
  const source = raw?.data?.data || raw?.data || [];
  if (Array.isArray(source)) {
    return source;
  }
  if (Array.isArray(source?.enrollments)) {
    return source.enrollments;
  }
  if (source?.enrollment) {
    return [source.enrollment];
  }
  return source ? [source] : [];
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const authData = JSON.parse(sessionStorage.getItem("customer_scheme_auth") || "{}");
  const customerMasterId = authData?.customerMasterId;
  const customerId = authData?.customerId;
  const customerPhone = authData?.phone;
  const customerMobile = authData?.mobile;
  const customerNameFromAuth = authData?.customerName;
  const applicationNumberFromAuth = authData?.applicationNumber;
  const identityId = customerMasterId || customerId;
  const identityCandidates = useMemo(
    () =>
      Array.from(
        new Set([customerMasterId, customerId, customerPhone, customerMobile].filter(Boolean))
      ),
    [customerMasterId, customerId, customerPhone, customerMobile]
  );
  const enrollmentCacheKey = identityId
    ? `customer_scheme_enrollments_cache_${identityId}`
    : "customer_scheme_enrollment_cache";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [enrollments, setEnrollments] = useState([]);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState("");
  const [dashboardPayments, setDashboardPayments] = useState([]);
  const [changePlanOpen, setChangePlanOpen] = useState(false);
  const [changePlanTerm, setChangePlanTerm] = useState("MONTHLY");
  const [changePlanAmount, setChangePlanAmount] = useState("");
  const [changePlanSubmitting, setChangePlanSubmitting] = useState(false);
  const [changePlanMessage, setChangePlanMessage] = useState("");

  const handleLogout = () => {
    localStorage.removeItem(enrollmentCacheKey);
    sessionStorage.removeItem("customer_scheme_auth");
    localStorage.removeItem("customer_scheme_auth");
    navigate("/customer/login");
  };

  useEffect(() => {
    const loadEnrollment = async () => {
      if (identityCandidates.length === 0) {
        setError("Customer session not found. Please login again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        let parsedEnrollments = [];

        for (const candidate of identityCandidates) {
          try {
            const response = await fetchCustomerEnrollment(candidate);
            parsedEnrollments = normalizeEnrollments(response);
            if (parsedEnrollments.length > 0) {
              break;
            }
          } catch (innerErr) {
            void innerErr;
          }
        }

        if (parsedEnrollments.length > 0) {
          setEnrollments(parsedEnrollments);
          setSelectedEnrollmentId((prev) => prev || String(parsedEnrollments[0]?._id || parsedEnrollments[0]?.id || ""));
          localStorage.setItem(enrollmentCacheKey, JSON.stringify(parsedEnrollments));
        } else {
          const cached = JSON.parse(localStorage.getItem(enrollmentCacheKey) || "[]");
          const cachedList = Array.isArray(cached) ? cached : [];
          setEnrollments(cachedList);
          setSelectedEnrollmentId((prev) => prev || String(cachedList[0]?._id || cachedList[0]?.id || ""));
        }
        setError("");
      } catch (err) {
        const cached = JSON.parse(localStorage.getItem(enrollmentCacheKey) || "[]");
        const cachedList = Array.isArray(cached) ? cached : [];
        if (cachedList.length > 0) {
          setEnrollments(cachedList);
          setSelectedEnrollmentId((prev) => prev || String(cachedList[0]?._id || cachedList[0]?.id || ""));
          setError("");
        } else {
          setError(getMessage(err, "Unable to fetch enrollment details."));
        }
      } finally {
        setLoading(false);
      }
    };

    loadEnrollment();
  }, [enrollmentCacheKey, identityCandidates]);

  const enrollment = useMemo(() => {
    if (!selectedEnrollmentId) {
      return enrollments[0] || null;
    }
    return (
      enrollments.find(
        (item) => String(item?._id || item?.id || "") === String(selectedEnrollmentId)
      ) || enrollments[0] || null
    );
  }, [enrollments, selectedEnrollmentId]);

  const paymentDetails = useMemo(() => getEnrollmentPaymentDetails(enrollment), [enrollment]);

  const metrics = useMemo(() => {
    const total = numberValue(enrollment?.totalAmount || enrollment?.total);
    const paymentPlan = enrollment?.paymentPlan || {};
    const productType = String(enrollment?.productType || enrollment?.schemeType || enrollment?.planType || "-").toUpperCase();
    return {
      schemeName: enrollment?.schemeName || enrollment?.scheme?.name || "-",
      productType: getPlanTypeLabel(productType),
      productCode: enrollment?.productCode || enrollment?.comboCode || "-",
      applicationNumber: getApplicationNumber(enrollment) || applicationNumberFromAuth || "-",
      selectedPaymentFrequency: paymentDetails.selectedPaymentFrequency,
      selectedPaymentAmount: paymentDetails.selectedPaymentAmount,
      payableNow: paymentDetails.payableNow,
      nextDueDate: paymentDetails.nextDueDate,
      mode: paymentDetails.mode,
      isFlexiblePayment: paymentDetails.isFlexiblePayment,
      total,
      paid: paymentDetails.paidAmount,
      remaining: paymentDetails.remainingAmount,
      status: enrollment?.status || "PENDING",
      paymentPlanOptions: paymentDetails.paymentPlanOptions,
      canRedeem: Boolean(paymentPlan?.canRedeem ?? enrollment?.canRedeem),
      availableCredit: numberValue(paymentPlan?.availableCredit ?? enrollment?.availableCredit),
      redeemedAmount: numberValue(paymentPlan?.redeemedAmount ?? enrollment?.redeemedAmount),
      redemptionStatus: String(paymentPlan?.redemptionStatus || enrollment?.redemptionStatus || "NOT_AVAILABLE"),
      isOpenValuePlan: productType === "OPEN_VALUE",
    };
  }, [enrollment, applicationNumberFromAuth, paymentDetails]);

  useEffect(() => {
    setChangePlanTerm(paymentDetails.selectedPaymentFrequency);
    setChangePlanAmount(String(paymentDetails.selectedPaymentAmount || ""));
    setChangePlanMessage("");
    setChangePlanOpen(false);
  }, [selectedEnrollmentId, paymentDetails.selectedPaymentFrequency, paymentDetails.selectedPaymentAmount]);

  const installments = useMemo(() => enrollment?.installments || [], [enrollment]);
  const paymentHistory = useMemo(() => {
    if (dashboardPayments.length > 0) {
      return dashboardPayments;
    }
    return enrollment?.paymentHistory || enrollment?.payments || [];
  }, [dashboardPayments, enrollment]);
  const paidPercentage = metrics.total > 0 ? Math.min((metrics.paid / metrics.total) * 100, 100) : 0;
  const hasEnrollments = enrollments.length > 0 && Boolean(enrollment);
  const customerPhoneDisplay = customerPhone || customerMobile || enrollment?.customerPhone || "-";
  const customerNameDisplay = customerNameFromAuth || enrollment?.customerName || "Customer";
  const enrollmentActionLabel = hasEnrollments ? "Add More Plan" : "Enroll New Plan";
  const hasPendingPayment = useMemo(
    () => paymentHistory.some((payment) => ["PENDING", "IN_PROGRESS", "PROCESSING"].includes(normalizePaymentStatus(payment?.status))),
    [paymentHistory]
  );
  const shouldShowInstallmentTable =
    metrics.selectedPaymentFrequency === "MONTHLY" && metrics.isFlexiblePayment !== true;
  const primaryPaymentCtaLabel = shouldShowInstallmentTable ? "Pay Next Due" : "Make Payment";
  const sectionPaymentCtaLabel = shouldShowInstallmentTable ? "Pay Next Due" : "Pay For Selected Plan";
  const paymentFlowLabel = shouldShowInstallmentTable ? "Monthly Schedule Flow" : "Flexible Payment Flow";
  const paymentCtaModeClass = shouldShowInstallmentTable
    ? "customer-btn-mode-monthly"
    : "customer-btn-mode-flexible";
  const flexibleCycleSummary = useMemo(() => {
    if (shouldShowInstallmentTable) {
      return null;
    }

    const cycleAmount = numberValue(metrics.selectedPaymentAmount);
    if (cycleAmount <= 0) {
      return null;
    }

    const paidCycles = Math.max(Math.floor(metrics.paid / cycleAmount), 0);
    const pendingCycles = Math.max(Math.ceil(metrics.remaining / cycleAmount), 0);
    const totalCycles = paidCycles + pendingCycles;
    const progressPercentage = totalCycles > 0
      ? Math.min(Math.round((paidCycles / totalCycles) * 100), 100)
      : 0;
    const pendingApprovals = paymentHistory.filter((item) =>
      ["PENDING", "IN_PROGRESS", "PROCESSING"].includes(normalizePaymentStatus(item?.status))
    ).length;
    const nextRecommendedAmount = pendingCycles > 0 ? cycleAmount : 0;

    return {
      cycleAmount,
      paidCycles,
      pendingCycles,
      totalCycles,
      progressPercentage,
      pendingApprovals,
      nextRecommendedAmount,
      cycleUnitLabel: getCycleUnitLabel(metrics.selectedPaymentFrequency),
    };
  }, [
    shouldShowInstallmentTable,
    metrics.selectedPaymentAmount,
    metrics.paid,
    metrics.remaining,
    metrics.selectedPaymentFrequency,
    paymentHistory,
  ]);
  const paymentStatusById = useMemo(() => {
    const map = {};
    paymentHistory.forEach((payment) => {
      const id = getPaymentIdValue(payment?._id || payment?.id);
      if (id) {
        map[id] = payment?.status;
      }
    });
    return map;
  }, [paymentHistory]);

  const paymentStatusByInstallmentNo = useMemo(() => {
    const map = {};
    paymentHistory.forEach((payment) => {
      const status = payment?.status;
      const numbers = Array.isArray(payment?.installmentNumbers)
        ? payment.installmentNumbers
        : Array.isArray(payment?.installmentsCovered)
          ? payment.installmentsCovered
          : [];

      numbers
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0)
        .forEach((value) => {
          map[value] = chooseStrongerStatus(map[value], status);
        });

      const singleNumber = Number(payment?.installmentNumber);
      if (Number.isInteger(singleNumber) && singleNumber > 0) {
        map[singleNumber] = chooseStrongerStatus(map[singleNumber], status);
      }
    });
    return map;
  }, [paymentHistory]);

  const installmentRows = useMemo(() => {
    return installments.map((item, index) => {
      const installmentNo = getInstallmentNumber(item, index);
      const statusMeta = getInstallmentStatusMeta(
        item,
        installmentNo,
        paymentStatusById,
        paymentStatusByInstallmentNo
      );
      return { item, index, installmentNo, statusMeta };
    });
  }, [installments, paymentStatusById, paymentStatusByInstallmentNo]);

  const nextPayableInstallmentNumber = useMemo(() => {
    const outstanding = installmentRows
      .filter((row) => !row.statusMeta.isPaid)
      .filter((row) => Number.isFinite(row.installmentNo))
      .sort((a, b) => a.installmentNo - b.installmentNo);

    if (outstanding.length === 0) {
      return null;
    }

    const firstOutstanding = outstanding[0];
    if (firstOutstanding.statusMeta.label === "Waiting for Approval") {
      return null;
    }

    return firstOutstanding.installmentNo;
  }, [installmentRows]);

  useEffect(() => {
    const loadDashboardPayments = async () => {
      if (!hasEnrollments) {
        setDashboardPayments([]);
        return;
      }

      try {
        const params = customerMasterId
          ? { customerMasterId }
          : customerId
            ? { customerId }
            : {};

        const response = await fetchPayments(params);
        const list = response?.data?.data || response?.data || [];
        const selectedId = String(enrollment?._id || enrollment?.id || "");
        const enrollmentIds = new Set(
          enrollments
            .map((item) => String(item?._id || item?.id || ""))
            .filter(Boolean)
        );

        const filtered = (Array.isArray(list) ? list : [])
          .filter((item) => {
            const itemEnrollmentId = String(item?.enrollmentId?.$oid || item?.enrollmentId || "");
            if (itemEnrollmentId && enrollmentIds.has(itemEnrollmentId)) {
              return true;
            }
            if (customerMasterId && String(item?.customerMasterId) === String(customerMasterId)) {
              return true;
            }
            if (customerId && String(item?.customerId) === String(customerId)) {
              return true;
            }
            return false;
          })
          .filter((item) => {
            if (!selectedId) {
              return true;
            }
            return String(item?.enrollmentId?.$oid || item?.enrollmentId || "") === selectedId;
          });

        setDashboardPayments(filtered);
      } catch (err) {
        void err;
        setDashboardPayments([]);
      }
    };

    loadDashboardPayments();
  }, [hasEnrollments, customerMasterId, customerId, enrollment, enrollments]);

  const handleChangePaymentPlan = async () => {
    if (!enrollment) {
      return;
    }

    try {
      setChangePlanSubmitting(true);
      setChangePlanMessage("");

      const nextAmount = normalizePositiveNumber(changePlanAmount);
      if (nextAmount <= 0) {
        throw new Error("Enter a valid payment amount.");
      }

      const nextPlanOptions = {
        ...metrics.paymentPlanOptions,
        [changePlanTerm]: nextAmount,
      };

      const response = await updateEnrollmentPaymentPlan({
        enrollmentId: enrollment?._id || enrollment?.id,
        selectedPaymentFrequency: changePlanTerm,
        selectedPaymentAmount: nextAmount,
        paymentPlanOptions: nextPlanOptions,
        isFlexiblePayment: true,
      });

      const data = response?.data?.data || response?.data || {};
      const updatedEnrollment = data?.enrollment || data;
      if (updatedEnrollment?._id || updatedEnrollment?.id) {
        const updatedId = String(updatedEnrollment?._id || updatedEnrollment?.id || "");
        setEnrollments((prev) => prev.map((item) => (
          String(item?._id || item?.id || "") === updatedId ? updatedEnrollment : item
        )));
      }

      setChangePlanMessage("Payment plan updated successfully.");
      setChangePlanOpen(false);
    } catch (err) {
      setChangePlanMessage(getMessage(err, "Unable to change payment plan."));
    } finally {
      setChangePlanSubmitting(false);
    }
  };

  return (
    <div className="customer-scheme-shell">
      <div className="customer-scheme-container">
        <div className="customer-dashboard-hero">
          <div>
            <div className="customer-hero-chip">Customer Scheme Dashboard</div>
            <h2 className="customer-hero-title">{metrics.schemeName}</h2>
            <p className="customer-subtitle">Track payment schedule, payment progress, and plan status.</p>
            <p className="customer-subtitle" style={{ marginTop: 6 }}>
              Name: {customerNameDisplay} | Phone: {customerPhoneDisplay}
            </p>
            <div className="customer-progress-wrap">
              <div className="customer-progress-label">Payment Progress: {paidPercentage.toFixed(0)}%</div>
              <div className="customer-progress-track">
                <div className="customer-progress-fill" style={{ width: `${paidPercentage}%` }} />
              </div>
            </div>
            <div className="customer-field" style={{ marginTop: 12, maxWidth: 380 }}>
              <label>Active Scheme Enrollment</label>
              <select
                value={String(selectedEnrollmentId || enrollment?._id || enrollment?.id || "")}
                onChange={(e) => setSelectedEnrollmentId(e.target.value)}
              >
                {enrollments.length === 0 ? (
                  <option value="">No enrollment found</option>
                ) : (
                  enrollments.map((item, idx) => {
                    const value = String(item?._id || item?.id || `enr-${idx}`);
                    const label = `${item?.schemeName || "Scheme"} - ${item?.productCode || item?.comboCode || "-"}`;
                    return (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    );
                  })
                )}
              </select>
            </div>
          </div>
          <div className="customer-dashboard-actions">
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/enroll")}>{enrollmentActionLabel}</button>
            <button
              className={`customer-btn customer-btn-secondary ${paymentCtaModeClass}`}
              onClick={() => navigate(`/customer/payment?enrollmentId=${encodeURIComponent(enrollment?._id || enrollment?.id || "")}`)}
              disabled={!hasEnrollments}
            >
              {primaryPaymentCtaLabel}
            </button>
            <button
              className="customer-btn customer-btn-secondary"
              onClick={() =>
                navigate(
                  `/customer/redeem?enrollmentId=${encodeURIComponent(enrollment?._id || enrollment?.id || "")}`
                )
              }
              disabled={!hasEnrollments || !metrics.isOpenValuePlan}
            >
              Redeem Products
            </button>
            <button
              className="customer-btn customer-btn-secondary"
              onClick={() => setChangePlanOpen((prev) => !prev)}
              disabled={!hasEnrollments || !metrics.isFlexiblePayment || hasPendingPayment || changePlanSubmitting}
            >
              Change Payment Plan
            </button>
            <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/history")}>View Payment History</button>
            <button className="customer-btn customer-btn-secondary" onClick={handleLogout}>Logout</button>
          </div>
        </div>

        {loading ? (
          <div className="customer-card">Loading dashboard...</div>
        ) : error ? (
          <div className="customer-card customer-error">{error}</div>
        ) : !hasEnrollments ? (
          <div className="customer-card">
            <h3 className="customer-section-title">No Scheme Enrollments Yet</h3>
            <p className="customer-subtitle">
              You are logged in successfully, but no scheme is linked to this customer yet. Enroll a scheme to start tracking payment schedule and payments.
            </p>
            <div className="customer-row" style={{ marginTop: 16 }}>
              <button className="customer-btn customer-btn-primary" onClick={() => navigate("/customer/enroll")}>
                {enrollmentActionLabel}
              </button>
              <button className="customer-btn customer-btn-secondary" onClick={() => navigate("/customer/history")}>
                View Payment History
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="customer-dashboard-grid">
              <div className="customer-metric"><div className="customer-metric-label">Product Type</div><div className="customer-metric-value">{metrics.productType}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Product Code</div><div className="customer-metric-value">{metrics.productCode}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Application Number</div><div className="customer-metric-value">{metrics.applicationNumber}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Mode</div><div className="customer-metric-value">{metrics.mode}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Plan</div><div className="customer-metric-value">{metrics.selectedPaymentFrequency}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Pay Now</div><div className="customer-metric-value">Rs. {metrics.payableNow || metrics.selectedPaymentAmount}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Next Due</div><div className="customer-metric-value">{formatDisplayDate(metrics.nextDueDate)}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Total Amount</div><div className="customer-metric-value">Rs. {metrics.total}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Paid Amount</div><div className="customer-metric-value">Rs. {metrics.paid}</div></div>
              <div className="customer-metric"><div className="customer-metric-label">Remaining Amount</div><div className="customer-metric-value">Rs. {metrics.remaining}</div></div>
              <div className="customer-metric customer-metric-status"><div className="customer-metric-label">Status</div><div className="customer-metric-value">{getDisplayStatus(metrics.status)}</div></div>
            </div>

            {metrics.isOpenValuePlan && (
              <div className="customer-card customer-section-card" style={{ marginTop: 16 }}>
                <h3 className="customer-section-title">Savings Scheme Wallet</h3>
                <div className="customer-dashboard-grid">
                  <div className="customer-metric">
                    <div className="customer-metric-label">Wallet Total</div>
                    <div className="customer-metric-value">Rs. {metrics.total}</div>
                  </div>
                  <div className="customer-metric">
                    <div className="customer-metric-label">Redeemed</div>
                    <div className="customer-metric-value">Rs. {metrics.redeemedAmount}</div>
                  </div>
                  <div className="customer-metric">
                    <div className="customer-metric-label">Available Credit</div>
                    <div className="customer-metric-value">Rs. {metrics.availableCredit}</div>
                  </div>
                  <div className="customer-metric customer-metric-status">
                    <div className="customer-metric-label">Redemption Status</div>
                    <div className="customer-metric-value">{metrics.redemptionStatus}</div>
                  </div>
                </div>
                <div className="customer-row" style={{ marginTop: 10 }}>
                  <button
                    className="customer-btn customer-btn-primary"
                    onClick={() =>
                      navigate(
                        `/customer/redeem?enrollmentId=${encodeURIComponent(enrollment?._id || enrollment?.id || "")}`
                      )
                    }
                  >
                    Redeem Products
                  </button>
                </div>
                {!metrics.canRedeem && (
                  <div className="customer-helper-text" style={{ marginTop: 8 }}>
                    Redemption is currently locked for this enrollment. You can still open the redemption page to view eligibility and history.
                  </div>
                )}
              </div>
            )}

            <div className={`customer-flow-chip ${shouldShowInstallmentTable ? "customer-flow-chip-monthly" : "customer-flow-chip-flexible"}`}>
              {paymentFlowLabel}
            </div>

            {!shouldShowInstallmentTable && flexibleCycleSummary && (
              <div className="customer-card customer-section-card">
                <h3 className="customer-section-title">Cycle Progress</h3>
                <div className="customer-dashboard-grid" style={{ marginBottom: 10 }}>
                  <div className="customer-metric">
                    <div className="customer-metric-label">Paid Cycles</div>
                    <div className="customer-metric-value">{flexibleCycleSummary.paidCycles}</div>
                  </div>
                  <div className="customer-metric">
                    <div className="customer-metric-label">Pending Cycles</div>
                    <div className="customer-metric-value">{flexibleCycleSummary.pendingCycles}</div>
                  </div>
                  <div className="customer-metric">
                    <div className="customer-metric-label">Pending Approvals</div>
                    <div className="customer-metric-value">{flexibleCycleSummary.pendingApprovals}</div>
                  </div>
                  <div className="customer-metric">
                    <div className="customer-metric-label">Per Cycle Amount</div>
                    <div className="customer-metric-value">Rs. {flexibleCycleSummary.cycleAmount}</div>
                  </div>
                </div>
                <div className="customer-helper-text" style={{ marginBottom: 6 }}>
                  {`Cycle Unit: ${flexibleCycleSummary.cycleUnitLabel} | Total Cycles: ${flexibleCycleSummary.totalCycles}`}
                </div>
                <div className="customer-progress-track">
                  <div
                    className="customer-progress-fill"
                    style={{ width: `${flexibleCycleSummary.progressPercentage}%` }}
                  />
                </div>
                <div className="customer-helper-text" style={{ marginTop: 6 }}>
                  {`Progress: ${flexibleCycleSummary.progressPercentage}%`}
                </div>
                <div className="customer-helper-text" style={{ marginTop: 6, fontWeight: 600 }}>
                  {flexibleCycleSummary.pendingCycles > 0
                    ? `Next recommended payment: Rs. ${flexibleCycleSummary.nextRecommendedAmount} for 1 ${flexibleCycleSummary.cycleUnitLabel.slice(0, -1) || "cycle"}`
                    : "All due cycles are completed."}
                </div>
              </div>
            )}

            {(changePlanOpen || changePlanMessage) && (
              <div className="customer-card customer-section-card" style={{ marginTop: 16 }}>
                <h3 className="customer-section-title">Change Payment Plan</h3>
                {changePlanMessage && (
                  <div className={changePlanMessage.includes("successfully") ? "customer-success" : "customer-error"}>
                    {changePlanMessage}
                  </div>
                )}
                {changePlanOpen && (
                  <div className="customer-form">
                    <div className="customer-field">
                      <label>Payment Frequency</label>
                      <select value={changePlanTerm} onChange={(e) => setChangePlanTerm(e.target.value)}>
                        {PAYMENT_FREQUENCIES.map((term) => (
                          <option key={term} value={term}>{term}</option>
                        ))}
                      </select>
                    </div>
                    <div className="customer-field">
                      <label>Selected Payment Amount</label>
                      <input type="number" min="0" step="0.01" value={changePlanAmount} onChange={(e) => setChangePlanAmount(e.target.value)} />
                    </div>
                    <div className="customer-row">
                      <button className="customer-btn customer-btn-primary" type="button" onClick={handleChangePaymentPlan} disabled={changePlanSubmitting || hasPendingPayment}>
                        {changePlanSubmitting ? "Saving..." : "Save Payment Plan"}
                      </button>
                      <button className="customer-btn customer-btn-secondary" type="button" onClick={() => setChangePlanOpen(false)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {shouldShowInstallmentTable ? (
              <div className="customer-card customer-table-wrap customer-section-card">
                <h3 className="customer-section-title">Payment Schedule</h3>
                <table className="customer-table">
                  <thead>
                    <tr>
                      <th>Cycle No</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {installments.length === 0 ? (
                      <tr><td colSpan="4">No payment schedule available.</td></tr>
                    ) : (
                      installmentRows.map(({ item, index, installmentNo, statusMeta }) => {
                        const isWaitingForApproval = statusMeta.label === "Waiting for Approval";
                        const canPayNow =
                          !isWaitingForApproval &&
                          !statusMeta.isPaid &&
                          nextPayableInstallmentNumber !== null &&
                          installmentNo === nextPayableInstallmentNumber;

                        return (
                          <tr key={item?._id || item?.id || index}>
                            <td>{installmentNo}</td>
                            <td>Rs. {numberValue(item?.amount || metrics.selectedPaymentAmount)}</td>
                            <td>
                              <span className={statusMeta.className}>
                                {statusMeta.label}
                              </span>
                            </td>
                            <td>
                              {canPayNow ? (
                                <button
                                  className="customer-btn customer-btn-primary"
                                  onClick={() =>
                                    navigate(
                                      `/customer/payment?enrollmentId=${encodeURIComponent(enrollment?._id || enrollment?.id || "")}`
                                    )
                                  }
                                >
                                  Pay Now
                                </button>
                              ) : (
                                <span style={{ color: "#6b8196" }}>-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="customer-card customer-table-wrap customer-section-card">
                <h3 className="customer-section-title">Payment History</h3>
                <div className="customer-row" style={{ justifyContent: "space-between", marginBottom: 12 }}>
                  <p className="customer-subtitle" style={{ margin: 0 }}>
                    Daily and weekly plans are paid from the payment page.
                  </p>
                  <button
                    className={`customer-btn customer-btn-primary ${paymentCtaModeClass}`}
                    onClick={() =>
                      navigate(
                        `/customer/payment?enrollmentId=${encodeURIComponent(enrollment?._id || enrollment?.id || "")}`
                      )
                    }
                    disabled={!hasEnrollments}
                  >
                    {sectionPaymentCtaLabel}
                  </button>
                </div>
                <table className="customer-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.length === 0 ? (
                      <tr><td colSpan="3">No payment history available.</td></tr>
                    ) : (
                      paymentHistory.map((item, index) => (
                        <tr key={item?._id || item?.id || index}>
                          <td>{item?.paymentDate ? new Date(item.paymentDate).toLocaleDateString() : item?.date ? new Date(item.date).toLocaleDateString() : "-"}</td>
                          <td>Rs. {numberValue(item?.paidAmount || item?.amount)}</td>
                          <td>{getDisplayStatus(item?.status)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
